
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Image as ImageIcon, X, Star, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi, storageApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import FileUpload from "@/components/common/FileUpload";
// Image removed - use img tag;

interface UploadedFile {
  url: string;
  s3_key: string;
  file: File;
  preview?: string;
}

interface ProductImage {
  id: number;
  image_url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

export default function ProductDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const productId = parseInt(params.id as string);

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<ProductImage[]>([]);

  useEffect(() => {
    if (token && productId) {
      fetchProduct();
    }
  }, [token, productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getProduct(productId, token!);
      setProduct(data);
      setImages(data.images || []);
    } catch (error: any) {
      console.error("Error fetching product:", error);
      toast.error(error.response?.data?.detail || "Failed to load product");
      navigate("/admin/products");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files: UploadedFile[]) => {
    if (!token || files.length === 0) return;

    setUploading(true);
    try {
      // Upload each file sequentially to avoid overwhelming the server
      for (const file of files) {
        // Get presigned URL
        const uploadData = await storageApi.getUploadUrl({
          file_name: file.file.name,
          file_type: file.file.type,
          file_size: file.file.size,
          product_id: productId,
        });

        // Upload to S3 using presigned POST
        const formData = new FormData();
        Object.entries(uploadData.upload_fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        // Add the file - must be last for S3 presigned POST
        formData.append("file", file.file);

        const uploadResponse = await fetch(uploadData.upload_url, {
          method: "POST",
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.file.name}`);
        }

        // Notify backend that upload is complete (creates ProductImage record)
        await storageApi.uploadComplete({
          s3_key: uploadData.s3_key,
          product_id: productId,
          alt_text: file.file.name,
        });
      }

      toast.success(`Successfully uploaded ${files.length} image(s)`);
      // Refresh product data to get updated images
      await fetchProduct();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!token) return;
    if (!confirm("Are you sure you want to delete this image?")) return;

    try {
      // TODO: Add delete image API endpoint
      // await adminApi.deleteProductImage(productId, imageId, token);
      toast.success("Image deleted successfully");
      await fetchProduct();
    } catch (error: any) {
      console.error("Error deleting image:", error);
      toast.error(error.response?.data?.detail || "Failed to delete image");
    }
  };

  const handleSetPrimary = async (imageId: number) => {
    if (!token) return;

    try {
      // TODO: Add set primary image API endpoint
      // await adminApi.setPrimaryImage(productId, imageId, token);
      toast.success("Primary image updated");
      await fetchProduct();
    } catch (error: any) {
      console.error("Error setting primary image:", error);
      toast.error(error.response?.data?.detail || "Failed to update primary image");
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-4" />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="h-64 bg-gray-200 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/products"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">{product.name}</h1>
        <p className="text-xs text-gray-500">Product ID: {product.id} | Slug: {product.slug}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Product Images Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <ImageIcon className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Product Images</h2>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Upload Area */}
              <FileUpload
                onUploadComplete={handleImageUpload}
                multiple={true}
                maxFiles={10}
                accept="image/*"
                productId={productId}
              />

              {/* Existing Images */}
              {images.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 mb-3">Uploaded Images</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {images.map((img) => (
                      <div
                        key={img.id}
                        className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-300 transition-all"
                      >
                        <img
                          src={img.image_url}
                          alt={img.alt_text || "Product image"}
                          
                          className="object-cover"
                        />
                        {img.is_primary && (
                          <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <Star className="w-3 h-3 -current" />
                            Primary
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          {!img.is_primary && (
                            <button
                              onClick={() => handleSetPrimary(img.id)}
                              className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                            >
                              Set Primary
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteImage(img.id)}
                            className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <Package className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Product Details</h2>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Price</p>
                  <p className="text-sm font-bold text-gray-900">₹{product.price?.toLocaleString() || "0"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Stock</p>
                  <p className="text-sm font-bold text-gray-900">{product.stock || 0}</p>
                </div>
              </div>
              {product.description && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-700">{product.description}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <h3 className="text-xs font-semibold text-gray-700 mb-3">Status</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Active</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    product.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {product.is_active ? "Yes" : "No"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Featured</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    product.is_featured
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {product.is_featured ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
